/**
 * Recording a car's name for the race announcer.
 *
 * Three things happen here that are not obvious, and each is there because of something that would
 * otherwise break on a television rather than in a browser.
 *
 * **A beep, then recording — in that order.** The beep is what tells somebody the microphone is live,
 * because a phone gives no other signal and the alternative is people talking before it started and
 * re-recording. It finishes before capture begins so it is not itself recorded; a beep on the front of
 * every clip would be played back over the anthem eighty-four times a jam.
 *
 * **The recording is re-encoded to linear PCM before upload.** `MediaRecorder` gives Safari AAC in MP4 and
 * Chrome Opus in WebM, and Core Audio does not decode Opus on tvOS at all — the champion anthem arrived
 * that way and would have been silent with no error. Decoding in the browser and re-encoding as WAV
 * sidesteps the codec question completely: whatever was captured, what reaches S3 is PCM. It also avoids
 * needing ffmpeg in Lambda, which this project has nowhere.
 *
 * **It is levelled to match.** The bundled phrases sit at -18 dBFS RMS with peaks held at -1.5, and a clip
 * recorded at arm's length in a kitchen will not. Since the samples are already decoded here, measuring
 * and correcting is a loop over a Float32Array — and getting it wrong is the single most likely complaint
 * about the feature, because a name 6 dB down inside a sentence sounds like a mistake.
 */

/** Matches `TARGET_RMS_DBFS` in `scripts/build-announcer.py`. The two must agree or clips will not. */
const TARGET_RMS_DBFS = -18;
/** Matches `TRUE_PEAK`. Speech peaks far above its RMS, so this is usually what binds. */
const PEAK_CEILING_DBFS = -1.5;
/** Below this a "recording" is a room with nobody in it, and uploading it would be worse than not. */
const SILENCE_FLOOR_DBFS = -45;

/**
 * Speech detection, for cropping a held button down to the name that was said.
 *
 * Somebody holding a button says the name somewhere in the middle, and the clip has to be only that: these
 * are scheduled 90 ms apart, so two seconds of room tone on the front of "Grave Digger" turns an
 * announcement into a series of pauses.
 *
 * `FRAME_MS` — energy is measured per frame rather than per sample. A per-sample threshold triggers on the
 * first thing louder than the floor, which for a real recording is a lip smack, a click, or a knock, and
 * everything after it then survives.
 *
 * `RELATIVE_DROP_DB` — the threshold sits this far below the loudest frame rather than at a fixed level.
 * A fixed one cannot work: the same -45 dBFS is inaudible in a quiet room and *below the noise floor* of a
 * garage, where it would trim nothing at all.
 *
 * `MIN_RUN_FRAMES` — speech has to stay above the threshold for this many consecutive frames to count.
 * This is what actually rejects the click; a transient is one or two frames, a syllable is ten.
 *
 * **The two ends are not symmetric, and the first version treating them as such is why recordings came back
 * clipped.** Speech begins abruptly and ends by decaying: the tail of a vowel, and especially of a fricative
 * like the "r" in "Digger" or the "sh" in "Bone Shaker", fades over a couple of hundred milliseconds. A
 * single threshold that correctly ignores room noise at the start will cut through that tail while it is
 * still clearly audible, and the name arrives sounding bitten off — which is exactly what was reported.
 *
 * So: the offset threshold sits lower than the onset one (`OFFSET_DROP_DB` against `RELATIVE_DROP_DB`),
 * which is the usual hysteresis every voice-activity detector uses; the run required to *end* speech is
 * shorter than the run required to start it, because a tail is quiet by nature and demanding four loud
 * frames of it is demanding it not be a tail; and the trailing pad is several times the leading one. The
 * asymmetry costs nothing — trailing room tone at -45 dBFS is inaudible, and clips are scheduled 90 ms
 * apart anyway — while the alternative is audible on every single name.
 */
const FRAME_MS = 10;
const RELATIVE_DROP_DB = 25;
const OFFSET_DROP_DB = 38;
const MIN_RUN_FRAMES = 4;
const MIN_RUN_FRAMES_END = 2;
const PAD_MS = 40;
const TAIL_PAD_MS = 200;
/** The announcer speaks these back to back; anything longer is a mistake rather than a name. */
export const MAX_SECONDS = 6;

const BEEP_HZ = 880;
const BEEP_SECONDS = 0.12;
/** A breath of silence after the beep, so its decay is not on the front of the recording. */
const BEEP_TAIL_SECONDS = 0.06;

/**
 * How long to keep recording after the button is released.
 *
 * **Because people let go as they finish the word, not after it.** Hold-to-record makes the release part of
 * saying the name: the hand moves while the last syllable is still sounding, so stopping the recorder on
 * the release means the tail was never captured at all — no amount of careful trimming can recover audio
 * that does not exist. This is the other half of why recordings came back clipped, and the half that
 * trimming alone could not have fixed.
 *
 * 300 ms is comfortably longer than a trailing fricative and short enough that nobody notices it.
 */
const RELEASE_TAIL_MS = 300;

export interface RecordedName {
  /** WAV bytes, 44.1 kHz mono, cropped to the spoken name and levelled. Upload as `audio/wav`. */
  blob: Blob;
  seconds: number;
  /** How long the button was held. Shown beside `seconds` so the crop is visible rather than implied. */
  rawSeconds: number;
  /** For showing what levelling did, and for deciding whether a take is worth keeping. */
  gainDb: number;
  peakDbfs: number;
}

/** Where speech starts and ends, in samples. Exported for tests. */
export interface SpeechBounds {
  start: number;
  end: number;
  /** Estimated noise floor and speech peak, in dBFS — useful for explaining a rejected take. */
  floorDbfs: number;
  peakDbfs: number;
}

export class RecordingTooQuietError extends Error {
  constructor() {
    super("That take was too quiet to hear — try again a little closer.");
    this.name = "RecordingTooQuietError";
  }
}

/**
 * Plays the "go" beep and resolves once it has finished.
 *
 * Takes the `AudioContext` rather than making one because Safari only allows an `AudioContext` to start
 * inside a user gesture, so the caller has to create it in the tap handler and reuse it.
 */
export async function playStartBeep(context: AudioContext): Promise<void> {
  if (context.state === "suspended") await context.resume();

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = BEEP_HZ;
  // A sine, because a square wave at this frequency through a phone speaker is unpleasant and this
  // sound fires once per car across eighty-four cars.
  oscillator.type = "sine";

  const now = context.currentTime;
  // Ramped rather than switched. A gain that steps from 0 to full is a click, which on a small speaker
  // is louder than the tone.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.25, now + 0.01);
  gain.gain.setValueAtTime(0.25, now + BEEP_SECONDS - 0.02);
  gain.gain.linearRampToValueAtTime(0, now + BEEP_SECONDS);

  oscillator.connect(gain).connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + BEEP_SECONDS);

  await new Promise<void>((resolve) => {
    oscillator.onended = () => resolve();
  });
  await new Promise((resolve) => setTimeout(resolve, BEEP_TAIL_SECONDS * 1000));
}

/** Which container this browser will actually give us. Only used for the capture, never for upload. */
function captureMimeType(): string | undefined {
  const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * An in-progress recording. `stop()` resolves with the finished, levelled clip.
 *
 * Split from a single `record(seconds)` call so the button can be hold-to-talk: the length of a car's name
 * is not known in advance, and a fixed window either cuts "White Convertible Muscle Car" short or leaves
 * four seconds of room noise after "Max-D".
 */
export interface ActiveRecording {
  stop: () => Promise<RecordedName>;
  cancel: () => void;
}

/**
 * Beeps, then starts recording from the microphone.
 *
 * The microphone is requested *before* the beep so that the permission prompt — which stops everything on
 * a first run — happens before the sound that means "speak now" rather than after it.
 */
export async function startRecording(context: AudioContext): Promise<ActiveRecording> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      // A phone in a garage, so noise suppression earns its place: a lower floor is the difference
      // between `findSpeechBounds` locating a name and giving up.
      echoCancellation: true,
      noiseSuppression: true,
      // **Off deliberately.** Automatic gain works against both halves of this file. It raises gain when
      // nobody is speaking, which lifts room tone towards the level of the name and blunts the very
      // contrast the crop depends on; and it makes the level arbitrary, which is pointless when `level`
      // normalises to a measured target anyway.
      autoGainControl: false,
      channelCount: 1,
    },
  });

  await playStartBeep(context);

  const mimeType = captureMimeType();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  recorder.start();

  const release = () => {
    for (const track of stream.getTracks()) track.stop();
  };

  const stopAt = Date.now() + MAX_SECONDS * 1000;
  const guard = setTimeout(
    () => {
      if (recorder.state === "recording") recorder.stop();
    },
    Math.max(0, stopAt - Date.now()),
  );

  return {
    stop: () =>
      new Promise<RecordedName>((resolve, reject) => {
        clearTimeout(guard);
        recorder.onstop = () => {
          release();
          const captured = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
          void level(captured, context).then(resolve, reject);
        };
        if (recorder.state === "recording") {
          // Keeps capturing past the release; see `RELEASE_TAIL_MS`. The extra audio is trimmed like any
          // other trailing silence if it turns out to be silence, so this costs nothing when the person
          // did stop speaking first.
          setTimeout(() => {
            if (recorder.state === "recording") recorder.stop();
          }, RELEASE_TAIL_MS);
        } else {
          recorder.onstop?.(new Event("stop"));
        }
      }),
    cancel: () => {
      clearTimeout(guard);
      recorder.onstop = () => release();
      if (recorder.state === "recording") recorder.stop();
      else release();
    },
  };
}

/** Decodes, crops to the spoken name, levels, and re-encodes as WAV. Exported for tests. */
export async function level(captured: Blob, context: AudioContext): Promise<RecordedName> {
  const decoded = await context.decodeAudioData(await captured.arrayBuffer());
  // Mono: a spoken name has no stereo content, and the announcer plays it into a stereo graph anyway.
  const samples = mixToMono(decoded);
  const rawSeconds = samples.length / decoded.sampleRate;

  const bounds = findSpeechBounds(samples, decoded.sampleRate);
  if (!bounds) throw new RecordingTooQuietError();
  // Copied rather than a subarray view, because the gain and limiter below write in place and a view
  // would mutate the caller's buffer — which matters once anything wants to re-crop a take.
  const spoken = samples.slice(bounds.start, bounds.end);

  // **Measured after cropping, and that ordering is load-bearing.** RMS over a clip that still had two
  // seconds of silence in it reads far too low, so the correction comes out too high and the name plays
  // louder than everything else. A failed crop would otherwise become a level fault as well.
  const rms = rmsDbfs(spoken);
  if (!Number.isFinite(rms) || rms < SILENCE_FLOOR_DBFS) throw new RecordingTooQuietError();

  const gainDb = TARGET_RMS_DBFS - rms;
  applyGain(spoken, gainDb);
  // Matches the limiter in `build-announcer.py`: level and ceiling are decoupled, so a sharp transient
  // does not hold the whole clip quiet. Hard-limiting a handful of samples on speech is inaudible.
  const peakDbfs = limitPeaks(spoken, PEAK_CEILING_DBFS);

  return {
    blob: encodeWav(spoken, decoded.sampleRate),
    seconds: spoken.length / decoded.sampleRate,
    rawSeconds,
    gainDb,
    peakDbfs,
  };
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) return Float32Array.from(buffer.getChannelData(0));
  const out = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < out.length; index += 1) {
      out[index] = (out[index] ?? 0) + (data[index] ?? 0);
    }
  }
  for (let index = 0; index < out.length; index += 1) {
    out[index] = (out[index] ?? 0) / buffer.numberOfChannels;
  }
  return out;
}

/**
 * Finds the spoken name inside a held-button recording. `null` when there is no speech in it.
 *
 * Somebody holds the button, pauses, says "Grave Digger", pauses again, and lets go. Only the middle is
 * wanted, and this locates it in four steps:
 *
 * 1. **Per-frame energy.** RMS over 10 ms frames, not per sample. A per-sample scan stops at the first
 *    thing above the threshold, which in a real recording is a lip smack or a knock rather than a word.
 * 2. **A noise floor estimated from the recording itself**, as the 20th-percentile frame. Absolute
 *    thresholds cannot work across a quiet kitchen and a garage with a fan running.
 * 3. **A threshold relative to the loudest frame**, `RELATIVE_DROP_DB` below it, and never below the
 *    measured floor plus a margin. Speech has enormous dynamic range within one word; anchoring to the
 *    peak is what makes one number work for a shout and a mumble.
 * 4. **A minimum run.** A frame only counts if `MIN_RUN_FRAMES` around it are also above threshold, which
 *    is what rejects the click that the old per-sample version tripped on.
 *
 * Only the head and tail are trimmed. Internal pauses are left alone deliberately: the closure in a stop
 * consonant, and the gap between "Grave" and "Digger", both look like silence and cutting them would make
 * the name arrive clipped or run together.
 */
export function findSpeechBounds(samples: Float32Array, sampleRate: number): SpeechBounds | null {
  const frameLength = Math.max(1, Math.floor((sampleRate * FRAME_MS) / 1000));
  const frameCount = Math.floor(samples.length / frameLength);
  if (frameCount < 1) return null;

  const energies = new Float64Array(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    let sum = 0;
    const from = frame * frameLength;
    for (let index = from; index < from + frameLength; index += 1) {
      const sample = samples[index] ?? 0;
      sum += sample * sample;
    }
    energies[frame] = 20 * Math.log10(Math.sqrt(sum / frameLength) + 1e-12);
  }

  const sorted = Array.from(energies).sort((a, b) => a - b);
  const floorDbfs = sorted[Math.floor(sorted.length * 0.2)] ?? -120;
  const peakDbfs = sorted[sorted.length - 1] ?? -120;
  if (peakDbfs < SILENCE_FLOOR_DBFS) return null;

  // **When floor and peak are close there is no silence to find**, and the clip is speech from end to
  // end. That case has to be handled before the two thresholds below, because `floorDbfs + 6` sits *above*
  // a uniform signal's own level and would reject the entire recording as containing nothing — which is
  // what it did to the first take that happened to start and end on a word.
  const separation = peakDbfs - floorDbfs;
  if (separation < 12) {
    return { start: 0, end: samples.length, floorDbfs, peakDbfs };
  }

  // Whichever is higher: a fixed drop below the peak, or clear of the measured floor. The first handles a
  // quiet room, the second stops a noisy one from having its own hiss counted as the name.
  const threshold = Math.max(peakDbfs - RELATIVE_DROP_DB, floorDbfs + 6);
  // Never below the measured floor, or the "tail" found would be the room rather than the voice.
  const offsetThreshold = Math.max(peakDbfs - OFFSET_DROP_DB, floorDbfs + 3);

  const voiced = (frame: number) => (energies[frame] ?? -120) >= threshold;
  const stillVoiced = (frame: number) => (energies[frame] ?? -120) >= offsetThreshold;
  const runStartsAt = (frame: number) => {
    for (let offset = 0; offset < MIN_RUN_FRAMES; offset += 1) {
      if (frame + offset >= frameCount || !voiced(frame + offset)) return false;
    }
    return true;
  };
  const runEndsAt = (frame: number) => {
    for (let offset = 0; offset < MIN_RUN_FRAMES_END; offset += 1) {
      if (frame - offset < 0 || !stillVoiced(frame - offset)) return false;
    }
    return true;
  };

  let firstFrame = -1;
  for (let frame = 0; frame < frameCount; frame += 1) {
    if (runStartsAt(frame)) {
      firstFrame = frame;
      break;
    }
  }
  let lastFrame = -1;
  for (let frame = frameCount - 1; frame >= 0; frame -= 1) {
    if (runEndsAt(frame)) {
      lastFrame = frame;
      break;
    }
  }
  // A recording whose only content is shorter than the minimum run — a cough, a tap on the phone.
  if (firstFrame < 0 || lastFrame < firstFrame) return null;

  const pad = Math.floor((sampleRate * PAD_MS) / 1000);
  const tailPad = Math.floor((sampleRate * TAIL_PAD_MS) / 1000);
  return {
    start: Math.max(0, firstFrame * frameLength - pad),
    end: Math.min(samples.length, (lastFrame + 1) * frameLength + tailPad),
    floorDbfs,
    peakDbfs,
  };
}

function rmsDbfs(samples: Float32Array): number {
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return 20 * Math.log10(Math.sqrt(sum / samples.length));
}

function applyGain(samples: Float32Array, gainDb: number): void {
  const factor = dbfsToAmplitude(gainDb);
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = (samples[index] ?? 0) * factor;
  }
}

/** Clamps to the ceiling and reports the resulting peak. */
function limitPeaks(samples: Float32Array, ceilingDb: number): number {
  const ceiling = dbfsToAmplitude(ceilingDb);
  let peak = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index] ?? 0;
    const clamped = sample > ceiling ? ceiling : sample < -ceiling ? -ceiling : sample;
    samples[index] = clamped;
    peak = Math.max(peak, Math.abs(clamped));
  }
  return 20 * Math.log10(peak);
}

function dbfsToAmplitude(dbfs: number): number {
  return 10 ** (dbfs / 20);
}

/** A 16-bit mono WAV. Small enough at a second that compressing it would only add a codec to worry about. */
export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const bytes = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(bytes);
  const ascii = (offset: number, text: string) => {
    for (let index = 0; index < text.length; index += 1) {
      view.setUint8(offset + index, text.charCodeAt(index));
    }
  };

  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits
  ascii(36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (const sample of samples) {
    // Asymmetric on purpose: 16-bit signed runs -32768..32767, and scaling both directions by 32768
    // overflows the positive end by one, which wraps to a full-scale negative click.
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, clamped < 0 ? clamped * 32768 : clamped * 32767, true);
    offset += 2;
  }
  return new Blob([bytes], { type: "audio/wav" });
}
